import { useEffect } from "react";
import { useSettings } from "@/hooks/useSettings";

/**
 * Keeps document.title, favicon, and the OG/Twitter meta tags in sync with
 * the current tenant's settings during SPA navigation.
 *
 * The webpack-dev-server middleware already injects per-tenant tags into the
 * initial HTML response (that's what link-preview scrapers see). This hook
 * picks up edits the salon owner makes in admin Settings WITHOUT requiring a
 * full page reload — and is also the fallback when the middleware path isn't
 * used (e.g. visiting the unknown-tenant fallback, mama-preview locally).
 */
function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.split("[")[0]);
    // Re-apply the disambiguating attribute (name="…" / property="…")
    const match = selector.match(/\[([^=]+)="([^"]+)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLinkHref(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function TenantMeta() {
  const { settings } = useSettings();

  useEffect(() => {
    if (!settings || Object.keys(settings).length === 0) return;
    const salonName = (settings.salon_name || "").trim();
    const tagline = (settings.tagline || "").trim();
    const title =
      (settings.meta_title || "").trim() ||
      (salonName ? `${salonName} — Book your appointment` : "");
    const description =
      (settings.meta_description || "").trim() ||
      tagline ||
      (salonName ? `Book your appointment at ${salonName} online.` : "");
    const image =
      (settings.meta_image_url || "").trim() ||
      (settings.logo_url || "").trim();
    const favicon =
      (settings.meta_favicon_url || "").trim() ||
      (settings.icon_logo_url || "").trim() ||
      (settings.logo_url || "").trim();
    const siteName =
      (settings.meta_site_name || salonName || "").trim();
    const keywords = (settings.meta_keywords || "").trim();

    if (title) document.title = title;
    if (description) setMeta('meta[name="description"]', "content", description);
    if (keywords) setMeta('meta[name="keywords"]', "content", keywords);
    if (siteName) setMeta('meta[property="og:site_name"]', "content", siteName);
    if (title) setMeta('meta[property="og:title"]', "content", title);
    if (description) setMeta('meta[property="og:description"]', "content", description);
    if (image) setMeta('meta[property="og:image"]', "content", image);
    if (title) setMeta('meta[name="twitter:title"]', "content", title);
    if (description) setMeta('meta[name="twitter:description"]', "content", description);
    if (image) setMeta('meta[name="twitter:image"]', "content", image);
    if (favicon) {
      setLinkHref("icon", favicon);
      setLinkHref("apple-touch-icon", favicon);
    }
  }, [settings]);

  return null;
}
